import { useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import Button from "@/components/button";
import OtpInput from "@/components/otp-input";
import PasswordInput from "@/components/password-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth";
import {
  usePasswordChangeCompleteMutation,
  usePasswordChangeInitMutation,
} from "@/queries/hooks/authHooks";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

type Step = "start" | "verify";

function validatePassword(pw: string, t: (k: string) => string): string | null {
  if (pw.length < 8) return t("passwordReset.passwordTooShort");
  if (!/[A-Z]/.test(pw)) return t("passwordReset.passwordNeedsUppercase");
  if (!/[a-z]/.test(pw)) return t("passwordReset.passwordNeedsLowercase");
  if (!/[0-9]/.test(pw)) return t("passwordReset.passwordNeedsNumber");
  if (!/[@$!%*?&]/.test(pw)) return t("passwordReset.passwordNeedsSpecial");
  return null;
}

export function ChangePasswordDialog() {
  const { t } = useTranslation("settings");
  const { t: tAuth } = useTranslation("auth");
  const auth = useAuth();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("start");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const initMutation = usePasswordChangeInitMutation();
  const completeMutation = usePasswordChangeCompleteMutation();

  const userPhone = auth.user?.phone || "";
  const maskedPhone = userPhone ? `****${userPhone.slice(-4)}` : "";

  const reset = () => {
    setStep("start");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const sendCode = async () => {
    setError(null);
    try {
      await initMutation.mutateAsync();
      setStep("verify");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : t("personal.password.sendError"),
      );
    }
  };

  const submit = async () => {
    setError(null);
    if (code.length !== 6) {
      setError(t("personal.password.enterCodeError"));
      return;
    }
    const validationError = validatePassword(newPassword, tAuth);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(tAuth("passwordReset.passwordsDoNotMatch"));
      return;
    }
    try {
      await completeMutation.mutateAsync({ code, newPassword });
      notify.success(t("personal.password.success"));
      handleOpenChange(false);
    } catch (err: unknown) {
      Logger.error("Password change failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      setError(
        err instanceof Error ? err.message : t("personal.password.changeError"),
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-garnet">
          {t("personal.password.title")}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border border-garnet/30 bg-fog px-4 py-3">
        <span className="body-small text-gray-500">
          {t("personal.password.subtitle")}
        </span>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              {t("personal.password.changeButton")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("personal.password.title")}</DialogTitle>
              <DialogDescription>
                {step === "start" ? (
                  maskedPhone ? (
                    <Trans
                      i18nKey="personal.password.startPrompt"
                      ns="settings"
                      values={{ phone: maskedPhone }}
                      components={{ strong: <strong /> }}
                    />
                  ) : (
                    t("personal.password.noPhone")
                  )
                ) : (
                  t("personal.password.verifyPrompt")
                )}
              </DialogDescription>
            </DialogHeader>

            {step === "start" ? (
              <div className="flex flex-col gap-4">
                {error && <p className="body-small text-red-500">{error}</p>}
                <Button
                  type="button"
                  variant="primary"
                  onClick={sendCode}
                  disabled={initMutation.isPending || !maskedPhone}
                >
                  {initMutation.isPending
                    ? t("personal.password.sending")
                    : t("personal.password.sendCode")}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <OtpInput maxLength={6} onComplete={(c) => setCode(c)} />
                <PasswordInput
                  label={tAuth("passwordReset.newPasswordLabel")}
                  placeholder={tAuth("passwordReset.passwordPlaceholder")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <PasswordInput
                  label={tAuth("passwordReset.repeatPasswordLabel")}
                  placeholder={tAuth("passwordReset.passwordPlaceholder")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submit();
                  }}
                />
                {error && <p className="body-small text-red-500">{error}</p>}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="link"
                    className="-ml-5"
                    onClick={sendCode}
                    disabled={initMutation.isPending}
                  >
                    {t("personal.password.resendCode")}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={submit}
                    disabled={completeMutation.isPending}
                  >
                    {completeMutation.isPending
                      ? t("personal.password.saving")
                      : t("personal.password.save")}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
